<template>
  <dashboard-section :bordered="$device.isMobileOrTablet">
    <div
        v-if="!$device.isMobile || editNote"
        slot="header"
        class="d-flex flex-1-1 ai-center jc-between">
      <span class="d-flex ai-center text-lg text-bold" selenium="job-details-notes-label">
        My Notes
      </span>
      <transition name="FADE" mode="out-in">
        <span
            v-if="$device.isNeitherMobileOrTablet && !editMode && editNote && !isFetching"
            class="d-flex">
          <a class="EDIT-NOTE" selenium="job-details-edit-note" @click.prevent="onEdit">
            <vk-icon name="pencil" class="ICON" />Edit
          </a>
          <a
              class="DELETE-NOTE"
              selenium="job-details-delete-note"
              @click.prevent="openDeleteConfirmationModal">
            <vk-icon name="trash" class="ICON" />Delete
          </a>
        </span>
      </transition>
    </div>
    <transition
        name="FADE"
        mode="out-in"
        @before-leave="onNoteBeforeLeave"
        @after-enter="onTextareaAfterEnter">
      <p v-if="!editMode && editNote" :id="`note-${job.id}`" class="NOTE">{{ editNote }}</p>
      <form
          v-else-if="editMode"
          :id="`textarea-${job.id}`"
          class="NOTES-TEXTAREA-CONTAINER"
          @submit.prevent="onSaveNotes">
        <textarea-input
            name="notes"
            ref="textarea"
            v-model="editNote"
            placeholder="Type your note..."
            class="NOTES-TEXTAREA"
            selenium="job-details-note-textarea"
            :initial-height="textareaInitialHeight"
        />
        <div class="CONTROLS">
          <a
              class="CANCEL-EDIT"
              selenium="job-details-cancel-button"
              @click.prevent="onCancel">
            Cancel
          </a>
          <button
              :disabled="!hasNoteChanges || isFetching"
              class="SAVE-BUTTON"
              selenium="job-details-save-button"
              :class="{ 'x-pulse': isFetching }">
            <span v-if="!isFetching">Save</span>
          </button>
        </div>
      </form>
      <span v-else-if="isFetching || isUninitialized" class="x-pulse PLACEHOLDER" />
      <a
          v-else
          class="ADD-NOTE ANCHOR-CONTROLS"
          selenium="job-details-add-note-button"
          @click.prevent="onEdit">
        <vk-icon name="plus-circle-outlined" class="ICON" />Add a note
      </a>
    </transition>
    <sliding-modal
        :is-open="$device.isMobile && editModeMobile"
        backdrop-class="bg-transparent"
        transition-name="SLIDE-RIGHT"
        @secondary="onCancel">
      <vk-modal-body slot="body" class="SHADOW">
        <form
            class="d-flex flex-1-1 fd-column"
            @submit.prevent="onSaveNotes">
          <vk-modal-header class="bd-b-gray30">
            <div class="d-flex jc-center ai-center">
              <a
                  class="MODAL-CANCEL-EDIT"
                  selenium="job-details-cancel-button"
                  @click.prevent="onCancel">
                Cancel
              </a>
              <span class="d-flex text-bold">My Notes</span>
              <button
                  class="MODAL-ADD-NOTE"
                  selenium="job-details-save-button"
                  :class="{ 'DISABLED': !hasNoteChanges || isFetching }">
                Save
              </button>
            </div>
          </vk-modal-header>
          <vk-modal-content class="d-flex">
            <textarea-input
                name="notes"
                v-model="editNote"
                placeholder="Type your note..."
                selenium="job-details-note-textarea"
                class="MODAL-TEXTAREA"
            />
          </vk-modal-content>
        </form>
      </vk-modal-body>
    </sliding-modal>
    <confirmation-modal
        :is-open="isDeleteConfirmationModalOpened"
        @secondary="closeDeleteConfirmationModal"
        selenium="job-details-delete-note-modal-header">
      Are you sure you want to delete this note?
      <button
          slot="primary"
          class="DELETE-BUTTON"
          selenium="job-details-delete-note-button"
          @click.prevent="onDeleteNotes">
        Delete
      </button>
    </confirmation-modal>
    <confirmation-modal
        :is-open="isNavigateWhileEditModalOpened"
        @secondary="closeEditConfirmationModal"
        @primary="confirmEditConfirmationModal">
      You have unsaved notes, are you sure you want to discard the changes?
    </confirmation-modal>
    <transition
        v-if="$device.isMobileOrTablet && !editMode && note.content"
        slot="footer"
        name="FADE"
        mode="out-in">
      <span class="d-flex flex-1-1 jc-between p-y-2">
        <a
            class="DELETE-NOTE"
            selenium="job-details-delete-note"
            @click.prevent="openDeleteConfirmationModal">
          <vk-icon name="trash" class="ICON" selenium="delete-icon" />Delete
        </a>
        <a class="EDIT-NOTE" selenium="job-details-edit-note" @click.prevent="onEdit">
          <vk-icon name="pencil" class="ICON" selenium="edit-icon" />Edit
        </a>
      </span>
    </transition>
  </dashboard-section>
</template>

<script>
import { mapGetters } from 'vuex';
import jobParams from '@/mixins/job-params';
import job from '@/mixins/job';
import note from '@/mixins/job/note';
import TextareaInput from '@/views/Dashboard/components/TextareaInput';
import SlidingModal from '@/views/Dashboard/components/SlidingModal';
import ConfirmationModal from '@/views/Dashboard/components/ConfirmationModal';
import DashboardSection from '@/views/Dashboard/components/DashboardSection';

export default {
  name: 'pro-notes',
  components: {
    TextareaInput,
    SlidingModal,
    ConfirmationModal,
    DashboardSection,
  },
  mixins: [
    jobParams,
    job,
    note,
  ],
  data () {
    return {
      editMode: false,
      editModeMobile: false,
      editNote: '',
      textareaInitialHeight: 0,
      isDeleteConfirmationModalOpened: false,
      isNavigateWhileEditModalOpened: false,
    };
  },
  computed: {
    ...mapGetters('account/pro/info', [
      'serviceProviderId',
    ]),
    // Used by note mixin.
    noteId () {
      return this.match.notes && this.match.notes[0];
    },
    isUninitialized () {
      return this.currentJobNotesMeta.status === this.$options.REQUEST_LIFECYCLE.UNINITIALIZED;
    },
    isFetching () {
      return this.currentJobNotesMeta.status === this.$options.REQUEST_LIFECYCLE.FETCHING;
    },
    hasNoteChanges () {
      return (this.note.content || '') !== (this.editNote || '');
    },
  },
  watch: {
    note () {
      if (!this.editMode) {
        this.editNote = this.note.content || '';
      }
    },
    serviceProviderId: {
      immediate: true,
      handler (val) {
        if (val) {
          this.getNotes();
        }
      },
    },
  },
  mounted () {
    this.unsubscribeRouterBeforeEach = this.$router.beforeEach((to, from, next) => {
      if (this.jobId !== to.params.jobId && this.hasNoteChanges) {
        this.isNavigateWhileEditModalOpened = true;
        this.$goToNext = next;
      } else {
        next();
      }
    });
  },
  destroyed () {
    this.unsubscribeRouterBeforeEach();
  },
  methods: {
    getNotes () {
      this.fetchNotes({
        jobId: this.job.id,
        params: {
          order_hash: this.job.order_hash,
          service_provider_id: this.serviceProviderId,
          skillsProfile: this.match.skills_profile,
        },
      });
    },
    closeEditConfirmationModal () {
      this.isNavigateWhileEditModalOpened = false;
      this.$goToNext(false);
    },
    confirmEditConfirmationModal () {
      this.$goToNext();
    },
    onEdit () {
      if (this.$device.isMobile) {
        this.editModeMobile = true;
      } else {
        this.editMode = true;
      }
    },
    onCancel () {
      this.editNote = this.note.content || '';
      this.editMode = false;
      this.editModeMobile = false;
    },
    onNoteBeforeLeave (el) {
      if (this.job && el.id === `note-${this.job.id}`) {
        const { height } = el.getBoundingClientRect();
        this.textareaInitialHeight = height;
      }
    },
    onTextareaAfterEnter (el) {
      if (this.job && el.id === `textarea-${this.job.id}`) {
        global.setTimeout(() => this.$refs.textarea.$el.focus(), 150);
      }
    },
    async onSaveNotes () {
      if (this.editNote) {
        await this.createOrUpdateNotes({
          jobId: this.job.id,
          noteId: this.note.id,
          params: {
            provider_notes: this.editNote,
            service_provider_id: this.serviceProviderId,
            order_hash: this.job.order_hash,
            skillsProfile: this.match.skills_profile,
          },
        });
      } else {
        await this.onDeleteNotes();
      }

      this.$events.$emit(this.$events.SAVE_NOTES, {
        jobId: this.job.id,
        content: this.editNote,
      });

      this.editMode = false;
      this.editModeMobile = false;
    },
    async onDeleteNotes () {
      await this.deleteNotes({
        order_hash: this.job.order_hash,
        noteId: this.note.id,
      });

      this.editNote = '';
      this.textareaInitialHeight = 0;

      this.closeDeleteConfirmationModal();
    },
    openDeleteConfirmationModal () {
      this.isDeleteConfirmationModalOpened = true;
    },
    closeDeleteConfirmationModal () {
      this.isDeleteConfirmationModalOpened = false;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  @import "~@/views/Dashboard/constants";

  .ICON {
    @include h4;
    margin-right: $unit/2;
  }

  .ADD-NOTE,
  .EDIT-NOTE,
  .DELETE-NOTE {
    @include link-primary;
  }

  .EDIT-NOTE,
  .CANCEL-EDIT {
    @include margin-right(0, $md: 3);
  }

  .ADD-NOTE,
  .EDIT-NOTE,
  .CANCEL-EDIT,
  .DELETE-NOTE {
    @include text-large;
    @include hover(text-decoration, none, none);
    display: flex;

    @include desktop {
      @include text-medium;
    }
  }

  .ADD-NOTE,
  .EDIT-NOTE,
  .DELETE-NOTE {
    align-items: center;
  }

  .ADD-NOTE {
    @include text-large;

    .ICON {
      margin-right: $unit;
    }
  }

  .EDIT-NOTE {
    .ICON {
      @include desktop {
        @include h5;
      }
    }
  }

  .CANCEL-EDIT {
    @include link-black;
    color: $color-gray80;
    margin-right: 3*$unit;
  }

  .NOTE {
    width: 100%;
    color: $color-gray70;
  }

  .NOTES-TEXTAREA-CONTAINER {
    position: relative;
    display: flex;
    flex: 1 1 auto;
  }

  .NOTES-TEXTAREA {
    display: flex;
    min-height: 13*$unit + 2px;
    height: 13*$unit;
    padding-top: $unit;
    padding-bottom: 9*$unit;
  }

  .NOTE,
  .NOTES-TEXTAREA,
  .MODAL-TEXTAREA {
    white-space: pre-wrap;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  .CONTROLS {
    @include absolute(auto, 2*$unit, 2*$unit, auto);
    display: flex;
    align-items: center;
    z-index: 1;
  }

  .SAVE-BUTTON {
    @include button-primary;
    @include button-small;
    width: 12*$unit;
    font-weight: 600;
  }

  .DELETE-BUTTON {
    @include button-primary;
    @include button-medium;
    display: flex;
    flex: 1 1 auto;
    margin-left: 1.5*$unit;
    font-weight: 600;
  }

  .PLACEHOLDER {
    @include rect(250px, 16px);
    margin: $unit/2 0;
  }

  .MODAL-CANCEL-EDIT,
  .MODAL-ADD-NOTE {
    @include absolute;
  }

  .MODAL-CANCEL-EDIT {
    @include link-black;
    @include text-large;
    left: 2*$unit;
  }

  .MODAL-ADD-NOTE {
    @include link-primary;
    @include text-large;
    right: 2*$unit;
    padding: 0;
    background: none;
    border: none;
    appearance: none;
    will-change: color;
    transition: color $transition-half-duration;

    &:focus {
      outline: none;
    }

    &.DISABLED {
      @include hover(color, $color-gray40, $color-gray40);
      pointer-events: none;
    }
  }

  .MODAL-TEXTAREA {
    display: flex;
    flex: 1 1 auto;
    padding: 0;
    border: none;
    overflow-y: scroll;
  }

  .SHADOW {
    @include responsive(box-shadow, 0 0 10px rgba(black, 0.2), $md: none);
  }

  .ANCHOR-CONTROLS {
    @include responsive(flex, 1 1 auto, $md: 0 1 auto);
    display: flex;
  }
</style>
